import React from 'react'
import './Prensa.css'
import PhotoLiner from '../../basic/photo-liner/PhotoLiner'
import SafeArea from '../../basic/safe-area'
import { RateStarSVG } from '../../../constants/svgs'
import { useI18n } from '../../../i18n/i18n'
import { dict } from '../../../i18n/dict'

type PrensaItem = (typeof dict.project.press.items)[number]

const Prensa: React.FC = () => {
    const { t, lang } = useI18n()

    const prensa: PrensaItem[] = [...dict.project.press.items];

    // ---------- DESKTOP SLIDER (4 per page) ----------
    const DESKTOP_PAGE_SIZE = 4

    const desktopPages = React.useMemo<PrensaItem[][]>(() => {
        const pages: PrensaItem[][] = []
        for (let i = 0; i < prensa.length; i += DESKTOP_PAGE_SIZE) {
            pages.push(prensa.slice(i, i + DESKTOP_PAGE_SIZE))
        }
        return pages
    }, [prensa])

    const [desktopPageIndex, setDesktopPageIndex] = React.useState(0)
    const totalDesktopPages = desktopPages.length

    const canGoPrevDesktop = desktopPageIndex > 0
    const canGoNextDesktop = desktopPageIndex < totalDesktopPages - 1

    const handlePrevDesktop = () => {
        if (!canGoPrevDesktop) return
        setDesktopPageIndex((prev) => Math.max(prev - 1, 0))
    }

    const handleNextDesktop = () => {
        if (!canGoNextDesktop) return
        setDesktopPageIndex((prev) => Math.min(prev + 1, totalDesktopPages - 1))
    }

    // touchpad horizontal scroll on desktop
    const desktopWheelAccumRef = React.useRef(0)
    const WHEEL_THRESHOLD = 40

    const handleDesktopWheel = (e: React.WheelEvent<HTMLDivElement>) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return

        e.preventDefault()
        desktopWheelAccumRef.current += e.deltaX

        if (desktopWheelAccumRef.current > WHEEL_THRESHOLD) {
            handleNextDesktop()
            desktopWheelAccumRef.current = 0
        } else if (desktopWheelAccumRef.current < -WHEEL_THRESHOLD) {
            handlePrevDesktop()
            desktopWheelAccumRef.current = 0
        }
    }

    // ---------- MOBILE SLIDER (1 at a time, infinite loop + swipe) ----------
    const [mobileIndex, setMobileIndex] = React.useState(0)

    const handleNextMobile = () => setMobileIndex((prev) => (prev + 1) % prensa.length)
    const handlePrevMobile = () => setMobileIndex((prev) => (prev - 1 + prensa.length) % prensa.length)

    const mobileViewportRef = React.useRef<HTMLDivElement | null>(null)
    const touchStartXRef = React.useRef<number | null>(null)
    const touchStartYRef = React.useRef<number | null>(null)
    const [mobileDragX, setMobileDragX] = React.useState(0)
    const [mobileDragging, setMobileDragging] = React.useState(false)
    const mobileWidthRef = React.useRef<number>(0)
    const MOBILE_SWIPE_THRESHOLD_RATIO = 0.2

    const handleTouchStartMobile = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0]
        touchStartXRef.current = touch.clientX
        touchStartYRef.current = touch.clientY
        setMobileDragging(true)

        mobileWidthRef.current = mobileViewportRef.current
            ? mobileViewportRef.current.clientWidth
            : window.innerWidth

        setMobileDragX(0)
    }

    const handleTouchMoveMobile = (e: React.TouchEvent<HTMLDivElement>) => {
        if (touchStartXRef.current == null || touchStartYRef.current == null) return

        const touch = e.touches[0]
        const diffX = touch.clientX - touchStartXRef.current
        const diffY = touch.clientY - touchStartYRef.current

        if (Math.abs(diffY) > Math.abs(diffX)) return
        setMobileDragX(diffX)
    }

    const handleTouchEndMobile = () => {
        if (!mobileWidthRef.current) {
            setMobileDragX(0)
            setMobileDragging(false)
            return
        }

        const threshold = mobileWidthRef.current * MOBILE_SWIPE_THRESHOLD_RATIO

        if (Math.abs(mobileDragX) > threshold) {
            if (mobileDragX < 0) handleNextMobile()
            else handlePrevMobile()
        }

        setMobileDragX(0)
        setMobileDragging(false)
        touchStartXRef.current = null
        touchStartYRef.current = null
    }

    const mobileBaseTranslate = -mobileIndex * 100
    const mobileDragPercent =
        mobileWidthRef.current > 0 ? (mobileDragX / mobileWidthRef.current) * 100 : 0
    const mobileTrackTranslate = mobileBaseTranslate + mobileDragPercent

    const seeMoreLabel = t('project.press.seeMore')
    const ariaPrevDesktop = t('project.press.aria.prevDesktop')
    const ariaNextDesktop = t('project.press.aria.nextDesktop')
    const ariaPrevMobile = t('project.press.aria.prevMobile')
    const ariaNextMobile = t('project.press.aria.nextMobile')

    return (
        <>
            {/* DESKTOP */}
            <div className="project-prensa-desktop">
                <PhotoLiner title={t('project.press.title')} color="#ffffff" showLine />
                <SafeArea>
                    <div className="prensa-slider">
                        <button
                            type="button"
                            className="prensa-arrow prensa-arrow-left"
                            onClick={handlePrevDesktop}
                            aria-label={ariaPrevDesktop}
                            disabled={!canGoPrevDesktop}
                        >
                            <svg width="auto" height="24" viewBox="0 0 18 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17 1L2 18L17 35" stroke="white" />
                            </svg>
                        </button>

                        <div className="prensa-slider-viewport" onWheel={handleDesktopWheel}>
                            <div
                                className="prensa-slider-track"
                                style={{
                                    transform: `translateX(-${desktopPageIndex * 100}%)`,
                                    transition: 'transform 0.5s ease',
                                }}
                            >
                                {desktopPages.map((page, pageIndex) => (
                                    <div key={pageIndex} className="prensa-slider-page">
                                        <div className="project-prensa-grid gap">
                                            {page.map((item) => (
                                                <div key={item.id} className="project-prensa-item textWhite">
                                                    <div>
                                                        <div className="up cormorant mb30 fs16" style={{ color: '#B0B0B0' }}>
                                                            {typeof item.name === 'string' ? item.name : item.name[lang]}
                                                        </div>

                                                        <div className="fs20 mb70">
                                                            {typeof item.text === 'string' ? item.text : item.text[lang]}
                                                        </div>
                                                    </div>

                                                    <div className="d-flex jcsb aic">
                                                        {item.url ? (
                                                            <a
                                                                className="d-flex gap8 aic see_more_url"
                                                                href={item.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <div dangerouslySetInnerHTML={{ __html: seeMoreLabel }}></div>
                                                            </a>
                                                        ) : (
                                                            <div />
                                                        )}

                                                        {item.rate && (
                                                            <div className="project-prensa-item-rate fs20">
                                                                {Array.from({ length: Number(item.rate) || 0 }).map((_, index) => (
                                                                    <RateStarSVG key={index} />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="prensa-arrow prensa-arrow-right"
                            onClick={handleNextDesktop}
                            aria-label={ariaNextDesktop}
                            disabled={!canGoNextDesktop}
                        >
                            <svg width="auto" height="24" viewBox="0 0 18 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L16 18L1 35" stroke="white" />
                            </svg>
                        </button>
                    </div>
                </SafeArea>
            </div>

            {/* MOBILE */}
            <div className="project-prensa-mobile">
                <PhotoLiner title={t('project.press.title')} color="#ffffff" showLine />
                <SafeArea>
                    <div
                        className="project-prensa-mobile-viewport"
                        ref={mobileViewportRef}
                        onTouchStart={handleTouchStartMobile}
                        onTouchMove={handleTouchMoveMobile}
                        onTouchEnd={handleTouchEndMobile}
                    >
                        <button
                            type="button"
                            className="prensa-mobile-arrow prensa-mobile-arrow-left"
                            onClick={handlePrevMobile}
                            aria-label={ariaPrevMobile}
                        >
                            <svg width="auto" height="24" viewBox="0 0 18 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17 1L2 18L17 35" stroke="white" />
                            </svg>
                        </button>

                        <div
                            className="project-prensa-mobile-track"
                            style={{
                                transform: `translateX(${mobileTrackTranslate}%)`,
                                transition: mobileDragging ? 'none' : 'transform 0.3s ease-out',
                            }}
                        >
                            {prensa.map((item) => (
                                <div key={item.id} className="project-prensa-mobile-slide">
                                    <div className="project-prensa-item textWhite">
                                        <div>
                                            <div className="up cormorant mb30 fs16" style={{ color: '#B0B0B0' }}>
                                                {typeof item.name === 'string' ? item.name : item.name[lang]}
                                            </div>

                                            <div className="fs20 mb70">
                                                {typeof item.text === 'string' ? item.text : item.text[lang]}
                                            </div>
                                        </div>

                                        <div className="d-flex jcsb aic">
                                            {item.url ? (
                                                <a className="d-flex gap8 aic" href={item.url} target="_blank" rel="noopener noreferrer">
                                                    <div dangerouslySetInnerHTML={{ __html: seeMoreLabel }}></div>
                                                </a>
                                            ) : (
                                                <div />
                                            )}

                                            {item.rate && (
                                                <div className="project-prensa-item-rate fs20">
                                                    {Array.from({ length: Number(item.rate) || 0 }).map((_, index) => (
                                                        <RateStarSVG key={index} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            type="button"
                            className="prensa-mobile-arrow prensa-mobile-arrow-right"
                            onClick={handleNextMobile}
                            aria-label={ariaNextMobile}
                        >
                            <svg width="auto" height="24" viewBox="0 0 18 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L16 18L1 35" stroke="white" />
                            </svg>
                        </button>
                    </div>
                </SafeArea>
            </div>
        </>
    )
}

export default Prensa
